'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { Badge } from '@sola/ark-core/atoms/badge/badge.tsx';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { IntlBlock } from '@sola/ark-core/atoms/intl/Intl.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { ChildImageVariantEnum } from '@sola/ark-core/utils/child-image.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import type { ChildAvailabilityItem } from '@sola/schema/dotcom/children/child.ts';
import { DepartureSubChildBioSheet, type SubChildBioSheetProps } from '../../organisms/departure-sub-child-bio-sheet/departure-sub-child-bio-sheet.tsx';
import { DepartureSubChildSearchSheet } from '../../organisms/departure-sub-child-search-sheet/departure-sub-child-search-sheet.tsx';
import { ChildSpotlightCard, ChildSpotlightCardActionItem, ChildSpotlightImage, ChildStats } from '../child-spotlight-card/child-spotlight-card.tsx';
import { useDepartureSubSpotlightCard } from './departure-sub-spotlight-card.logic.tsx';
export interface DepartureSubSpotlightCardProps {
  availability: SubChildBioSheetProps['availability'];
  childDetails: NonNullable<SubChildBioSheetProps['childDetails']>;
  departingChildDetails: NonNullable<SubChildBioSheetProps['childDetails']>;
  getAlternateChildPath: (childGlobalId: string) => string;
  onAccept: NonNullable<SubChildBioSheetProps['onAccept']>;
  onSelectAlternateChild: (childGlobalId: string) => void;
  variant?: 'offered' | 'alternate';
}
export const DepartureSubSpotlightCard: React.FC<DepartureSubSpotlightCardProps> = t0 => {
  const $ = _c(93);
  const {
    availability,
    childDetails,
    departingChildDetails,
    getAlternateChildPath,
    onAccept,
    onSelectAlternateChild,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "offered" : t1;
  let t2;
  if ($[0] !== availability || $[1] !== childDetails || $[2] !== departingChildDetails) {
    t2 = {
      availability,
      childDetails,
      departingChildDetails
    };
    $[0] = availability;
    $[1] = childDetails;
    $[2] = departingChildDetails;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    t,
    cardData
  } = useDepartureSubSpotlightCard(t2);
  const [isSearchSheetOpen, setIsSearchSheetOpen] = useState(false);
  let t3;
  if ($[4] !== onSelectAlternateChild) {
    t3 = child => {
      setIsSearchSheetOpen(false);
      onSelectAlternateChild(child.globalId);
    };
    $[4] = onSelectAlternateChild;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleAlternateChildSelect = t3;
  if (!cardData) {
    let t4;
    if ($[6] !== t) {
      t4 = t("my:sponsorshipRelationship.childBio.alert.title");
      $[6] = t;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    let t5;
    if ($[8] !== t) {
      t5 = t("my:sponsorshipRelationship.childBio.alert.message");
      $[8] = t;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    let t6;
    if ($[10] !== t4 || $[11] !== t5) {
      t6 = <Alert variant="negative" title={t4} message={t5} />;
      $[10] = t4;
      $[11] = t5;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    return t6;
  }
  let t4;
  if ($[13] !== cardData.childImageUrl || $[14] !== cardData.childName) {
    t4 = <ChildSpotlightImage childImageUrl={cardData.childImageUrl} alt={cardData.childName} variantMd={ChildImageVariantEnum.Full} />;
    $[13] = cardData.childImageUrl;
    $[14] = cardData.childName;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== cardData.waitingDays || $[17] !== t) {
    t5 = cardData.waitingDays ? <Badge color="brand" label={t("my:sponsorshipRelationship.departures.spotlight.sub.badge", {
      daysWaiting: cardData.waitingDays
    })} size="md" /> : undefined;
    $[16] = cardData.waitingDays;
    $[17] = t;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== cardData.childName || $[20] !== t) {
    t6 = t("my:sponsorshipRelationship.departures.spotlight.sub.title", {
      childName: cardData.childName
    });
    $[19] = cardData.childName;
    $[20] = t;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== t6) {
    t7 = <h2 className="ChildSpotlightCard__title m-none w-full heading-neutral-amplified heading-sm md:heading-md">{t6}</h2>;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  const t8 = `my:sponsorshipRelationship.departures.spotlight.sub.description.${variant}`;
  let t9;
  if ($[24] !== cardData.age || $[25] !== cardData.childName || $[26] !== cardData.country || $[27] !== cardData.family || $[28] !== cardData.favoriteActivities || $[29] !== cardData.previousChildName) {
    t9 = {
      age: cardData.age,
      childName: cardData.childName,
      country: cardData.country,
      family: cardData.family,
      favoriteActivities: cardData.favoriteActivities,
      previousChildName: cardData.previousChildName
    };
    $[24] = cardData.age;
    $[25] = cardData.childName;
    $[26] = cardData.country;
    $[27] = cardData.family;
    $[28] = cardData.favoriteActivities;
    $[29] = cardData.previousChildName;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      strong: <strong />
    };
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== cardData.genderContext || $[33] !== t8 || $[34] !== t9) {
    t11 = <div className="ChildSpotlightCard__description w-full body-neutral-mild body-sm md:body-md"><p><IntlBlock i18nKey={t8} context={cardData.genderContext} values={t9} components={t10} /></p></div>;
    $[32] = cardData.genderContext;
    $[33] = t8;
    $[34] = t9;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== t11 || $[37] !== t7) {
    t12 = <div className="ChildSpotlightCard__text flex w-full flex-col gap-comfy-xs">{t7}{t11}</div>;
    $[36] = t11;
    $[37] = t7;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== cardData.genderKey) {
    t13 = <ArkIcon iconName={cardData.genderKey} />;
    $[39] = cardData.genderKey;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== cardData.age || $[42] !== cardData.genderContext || $[43] !== t) {
    t14 = t("my:sponsorshipRelationship.departures.spotlight.sub.stats.ageGender", {
      age: cardData.age,
      context: cardData.genderContext
    });
    $[41] = cardData.age;
    $[42] = cardData.genderContext;
    $[43] = t;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] !== t13 || $[46] !== t14) {
    t15 = {
      icon: t13,
      label: t14
    };
    $[45] = t13;
    $[46] = t14;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  let t16;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <ArkIcon iconName="birthday-cake" />;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  let t17;
  if ($[49] !== cardData.birthDate || $[50] !== t) {
    t17 = t("my:sponsorshipRelationship.departures.spotlight.sub.stats.birthday", {
      date: cardData.birthDate
    });
    $[49] = cardData.birthDate;
    $[50] = t;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  let t18;
  if ($[52] !== t17) {
    t18 = {
      icon: t16,
      label: t17
    };
    $[52] = t17;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  let t19;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <ArkIcon iconName="globe-05" />;
    $[54] = t19;
  } else {
    t19 = $[54];
  }
  let t20;
  if ($[55] !== cardData.country) {
    t20 = {
      icon: t19,
      label: cardData.country
    };
    $[55] = cardData.country;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  let t21;
  if ($[57] !== t15 || $[58] !== t18 || $[59] !== t20) {
    t21 = <ChildStats items={[t15, t18, t20]} />;
    $[57] = t15;
    $[58] = t18;
    $[59] = t20;
    $[60] = t21;
  } else {
    t21 = $[60];
  }
  let t22;
  if ($[61] !== cardData.childName || $[62] !== t) {
    t22 = t("my:sponsorshipRelationship.departures.spotlight.sub.primaryAction", {
      childName: cardData.childName
    });
    $[61] = cardData.childName;
    $[62] = t;
    $[63] = t22;
  } else {
    t22 = $[63];
  }
  let t23;
  if ($[64] !== t22) {
    t23 = <ButtonStyle className="w-full @lg:w-auto inline-flex" size="lg" variant="secondary"><button type="button">{t22}</button></ButtonStyle>;
    $[64] = t22;
    $[65] = t23;
  } else {
    t23 = $[65];
  }
  let t24;
  if ($[66] !== availability || $[67] !== childDetails || $[68] !== onAccept || $[69] !== t23) {
    t24 = <ChildSpotlightCardActionItem className="w-full @lg:w-fit @lg:flex-none"><DepartureSubChildBioSheet availability={availability} childDetails={childDetails} onAccept={onAccept} openTrigger={t23} /></ChildSpotlightCardActionItem>;
    $[66] = availability;
    $[67] = childDetails;
    $[68] = onAccept;
    $[69] = t23;
    $[70] = t24;
  } else {
    t24 = $[70];
  }
  let t25;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <ArkIcon iconName="arrow-right" />;
    $[71] = t25;
  } else {
    t25 = $[71];
  }
  let t26;
  if ($[72] !== t) {
    t26 = t("my:sponsorshipRelationship.departures.spotlight.sub.secondaryAction");
    $[72] = t;
    $[73] = t26;
  } else {
    t26 = $[73];
  }
  let t27;
  if ($[74] !== t26) {
    t27 = <ButtonStyle className="w-full @lg:w-auto inline-flex" iconAfter={t25} size="lg" variant="tertiary"><button type="button">{t26}</button></ButtonStyle>;
    $[74] = t26;
    $[75] = t27;
  } else {
    t27 = $[75];
  }
  let t28;
  if ($[76] !== departingChildDetails || $[77] !== getAlternateChildPath || $[78] !== handleAlternateChildSelect || $[79] !== isSearchSheetOpen || $[80] !== t27) {
    t28 = <ChildSpotlightCardActionItem className="w-full @lg:w-fit @lg:flex-none"><DepartureSubChildSearchSheet departingChildDetails={departingChildDetails} getChildHref={getAlternateChildPath} onChildSelect={handleAlternateChildSelect} open={isSearchSheetOpen} onOpenChange={setIsSearchSheetOpen} openTrigger={t27} /></ChildSpotlightCardActionItem>;
    $[76] = departingChildDetails;
    $[77] = getAlternateChildPath;
    $[78] = handleAlternateChildSelect;
    $[79] = isSearchSheetOpen;
    $[80] = t27;
    $[81] = t28;
  } else {
    t28 = $[81];
  }
  let t29;
  if ($[82] !== t24 || $[83] !== t28) {
    t29 = <div className="ChildSpotlightCard__actions flex flex-col gap-snug-xl md:gap-snug-xl @lg:flex-row @lg:flex-wrap @lg:items-start">{t24}{t28}</div>;
    $[82] = t24;
    $[83] = t28;
    $[84] = t29;
  } else {
    t29 = $[84];
  }
  let t30;
  if ($[85] !== t12 || $[86] !== t21 || $[87] !== t29) {
    t30 = <div className="flex h-full flex-col justify-center gap-comfy-lg md:px-roomy-xs md:py-roomy-sm">{t12}{t21}{t29}</div>;
    $[85] = t12;
    $[86] = t21;
    $[87] = t29;
    $[88] = t30;
  } else {
    t30 = $[88];
  }
  let t31;
  if ($[89] !== t30 || $[90] !== t4 || $[91] !== t5) {
    t31 = <ChildSpotlightCard image={t4} badge={t5}>{t30}</ChildSpotlightCard>;
    $[89] = t30;
    $[90] = t4;
    $[91] = t5;
    $[92] = t31;
  } else {
    t31 = $[92];
  }
  return t31;
};